import type { VolunteerOrganization } from "@entities";
import { volunteerOrganizationRepo } from "@repositories";
import { AdvancedMarker, APIProvider, Pin } from "@vis.gl/react-google-maps";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  VOLUNTEER_ORG_EVENT_TYPES,
  VOLUNTEER_ORG_WARNING_LABELS,
  isVolunteerOrgBranch,
  volunteerOrgActivatableFilter,
  volunteerOrgActivationPhones,
  volunteerOrgActivationWarnings,
  volunteerOrgDisplayName,
  volunteerOrgEffectiveDomainIds,
  volunteerOrgEffectiveEventFlags,
  volunteerOrgHandlingManagerName,
  volunteerOrgHasOwnEventInterest,
  volunteerOrgIsActivatable,
} from "@shared/helpers/volunteer-organization";
import { hasValidCoordinates } from "@shared/helpers/geo";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { EntityFilter } from "remult";
import GenericMap from "../../../components/mission-map/GenericMap";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
} from "../../../constants/google-maps-browser";
import { DEFAULT_CENTER } from "../../mission-map/constants";
import { CommunicationRecordsDialog } from "../../../components/CommunicationRecordManager/CommunicationRecordsDialog";
import type { VolunteerOrgActivityDomain } from "@entities";
import PhoneNumberCell from "../PhoneNumberCell";
import {
  primaryActionSx,
  quietButtonSx,
  selectedCardSx,
} from "./volunteer-org-form-ui";

export const VOLUNTEER_ORG_BRANCH_INCLUDE = {
  parent: {
    include: {
      activities: { include: { domain: true } },
      handlingManager: true,
      contacts: true,
    },
  },
  contacts: true,
  handlingManager: true,
  activities: { include: { domain: true } },
  assignments: { include: { project: true } },
  branches: true,
} as const;

export function VolunteerOrgActivation({
  domains,
  filterWhere,
  onEditBranch,
  onEditParent,
}: {
  domains: VolunteerOrgActivityDomain[];
  filterWhere: EntityFilter<VolunteerOrganization>;
  onEditBranch: (branch: VolunteerOrganization) => void;
  onEditParent: (parent: VolunteerOrganization) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [commOpen, setCommOpen] = useState(false);

  const activatableWhere = useMemo((): EntityFilter<VolunteerOrganization> => {
    return {
      $and: [filterWhere, volunteerOrgActivatableFilter()],
    } as EntityFilter<VolunteerOrganization>;
  }, [filterWhere]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["volunteer-org-branches", JSON.stringify(activatableWhere)],
    queryFn: () =>
      volunteerOrganizationRepo.find({
        where: activatableWhere,
        orderBy: { name: "asc" },
        limit: 2000,
        include: VOLUNTEER_ORG_BRANCH_INCLUDE,
      }),
  });
  const branches = rows.filter(volunteerOrgIsActivatable);

  const selected = branches.find((row) => row.id === selectedId) ?? null;
  const pins = branches.filter((row) => hasValidCoordinates(row.coordinates));

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack gap={2} sx={{ p: { xs: 1.5, md: 2 }, pb: 3, minHeight: 0, flex: 1 }}>
      <Typography color="text.secondary" sx={{ fontSize: 13, px: 0.5 }}>
        {branches.length} ארגונים להפעלה
      </Typography>

      {branches.length === 0 ? (
        <Alert severity="info">
          אין ארגונים לפי הסינון. בדקו סוג אירוע, תחום, כתובת או סוג ארגון.
        </Alert>
      ) : (
        <Stack
          direction={{ xs: "column", lg: "row" }}
          gap={2}
          alignItems="stretch"
        >
          <Stack
            gap={1.25}
            sx={{ flex: 1, minWidth: 0, maxHeight: "70vh", overflow: "auto", pr: 0.5 }}
          >
            {branches.map((branch) => {
              const phones = volunteerOrgActivationPhones(branch, branch.parent);
              const domainIds = volunteerOrgEffectiveDomainIds(
                (branch.activities ?? []).map((row) => row.domainId),
                (branch.parent?.activities ?? []).map((row) => row.domainId),
              );
              const eventFlags = volunteerOrgEffectiveEventFlags(
                branch,
                branch.parent,
              );
              const eventsFromParent =
                !volunteerOrgHasOwnEventInterest(branch) &&
                volunteerOrgHasOwnEventInterest(eventFlags);
              const warnings = volunteerOrgActivationWarnings({
                phones,
                coordinates: branch.coordinates,
                flags: eventFlags,
                effectiveDomainIds: domainIds,
              });
              const domainNames = domains
                .filter((domain) => domainIds.includes(domain.id))
                .map((domain) => domain.name);
              const managerName = volunteerOrgHandlingManagerName(
                branch,
                branch.parent,
              );
              return (
                <Box
                  key={branch.id}
                  sx={selectedCardSx(selectedId === branch.id)}
                  onClick={() => setSelectedId(branch.id)}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 15.5 }}>
                    {volunteerOrgDisplayName(branch.name, branch.parent?.name)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.25 }}>
                    {branch.address || "אין כתובת"}
                    {branch.volunteerCapacity != null
                      ? ` · ${branch.volunteerCapacity} מתנדבים`
                      : ""}
                    {managerName ? ` · ${managerName}` : ""}
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.25 }}>
                    {VOLUNTEER_ORG_EVENT_TYPES.filter((item) => eventFlags[item.field]).map(
                      (item) => (
                        <Chip
                          key={item.id}
                          size="small"
                          label={
                            eventsFromParent ? `${item.label} · מהאב` : item.label
                          }
                        />
                      ),
                    )}
                    {domainNames.map((name) => (
                      <Chip key={name} size="small" variant="outlined" label={name} />
                    ))}
                    {warnings.map((warning) => (
                      <Chip
                        key={warning}
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={VOLUNTEER_ORG_WARNING_LABELS[warning]}
                      />
                    ))}
                  </Stack>
                  {phones.length > 0 ? (
                    <Stack gap={0.75} sx={{ mt: 1.25 }} onClick={(event) => event.stopPropagation()}>
                      {phones.slice(0, 2).map((phone, index) => (
                        <Stack key={`${phone.roleId}-${phone.phone}-${index}`} gap={0.25}>
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                            {phone.label}
                            {phone.name ? ` · ${phone.name}` : ""}
                            {phone.fromParent ? " · מהאב" : ""}
                          </Typography>
                          <PhoneNumberCell phoneNumber={phone.phone} />
                          {phone.email ? (
                            <Typography sx={{ fontSize: 12 }}>{phone.email}</Typography>
                          ) : null}
                        </Stack>
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
          <Box
            sx={{
              flex: 1.15,
              minHeight: 360,
              borderRadius: 2.5,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {pins.length === 0 ? (
              <Alert severity="info" sx={{ m: 2 }}>
                אין שלוחות עם נקודה במפה לפי הסינון
              </Alert>
            ) : (
              <APIProvider
                apiKey={GOOGLE_MAPS_API_KEY}
                libraries={GOOGLE_MAPS_LIBRARIES}
                language={GOOGLE_MAPS_LANGUAGE}
                region={GOOGLE_MAPS_REGION}
              >
                <Box sx={{ height: { xs: 360, lg: "70vh" } }}>
                  <GenericMap
                    defaultCenter={DEFAULT_CENTER}
                    defaultZoom={8}
                    style={{ width: "100%", height: "100%" }}
                  >
                    {pins.map((branch) => (
                      <AdvancedMarker
                        key={branch.id}
                        position={branch.coordinates!}
                        onClick={() => setSelectedId(branch.id)}
                      >
                        <Pin
                          background={selectedId === branch.id ? "#05204C" : "#00b4d8"}
                          borderColor="#fff"
                          glyphColor="#fff"
                        />
                      </AdvancedMarker>
                    ))}
                  </GenericMap>
                </Box>
              </APIProvider>
            )}
          </Box>
        </Stack>
      )}

      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 440 } },
        }}
      >
        {selected ? (
          <ActivationDrawer
            branch={selected}
            domains={domains}
            onClose={() => setSelectedId(null)}
            onEditBranch={() => onEditBranch(selected)}
            onEditParent={() => onEditParent(selected.parent ?? selected)}
            onLogCall={() => setCommOpen(true)}
          />
        ) : null}
      </Drawer>
      <CommunicationRecordsDialog
        open={commOpen}
        onClose={() => setCommOpen(false)}
        title={
          selected
            ? volunteerOrgDisplayName(selected.name, selected.parent?.name)
            : ""
        }
        volunteerOrganizationId={selected?.id}
        type="volunteer-organization"
      />
    </Stack>
  );
}

function ActivationDrawer({
  branch,
  domains,
  onClose,
  onEditBranch,
  onEditParent,
  onLogCall,
}: {
  branch: VolunteerOrganization;
  domains: VolunteerOrgActivityDomain[];
  onClose: () => void;
  onEditBranch: () => void;
  onEditParent: () => void;
  onLogCall: () => void;
}) {
  const phones = volunteerOrgActivationPhones(branch, branch.parent);
  const eventFlags = volunteerOrgEffectiveEventFlags(branch, branch.parent);
  const eventsFromParent =
    !volunteerOrgHasOwnEventInterest(branch) &&
    volunteerOrgHasOwnEventInterest(eventFlags);
  const domainIds = volunteerOrgEffectiveDomainIds(
    (branch.activities ?? []).map((row) => row.domainId),
    (branch.parent?.activities ?? []).map((row) => row.domainId),
  );
  const parentDomainIds = (branch.parent?.activities ?? []).map((row) => row.domainId);
  const assignments = (branch.assignments ?? []).filter(
    (row) => row.project?.isActive !== false,
  );
  const managerName = volunteerOrgHandlingManagerName(branch, branch.parent);

  return (
    <Stack sx={{ height: "100%" }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Stack sx={{ minWidth: 0, pr: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.3 }}>
            {volunteerOrgDisplayName(branch.name, branch.parent?.name)}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
            {branch.address || "אין כתובת"}
            {managerName ? ` · מנהל מלווה: ${managerName}` : ""}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} aria-label="סגירה" size="small">
          <CloseIcon />
        </IconButton>
      </Stack>
      <Stack gap={2.5} sx={{ p: 2.5, overflow: "auto", flex: 1 }}>
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {VOLUNTEER_ORG_EVENT_TYPES.filter((item) => eventFlags[item.field]).map(
            (item) => (
              <Chip
                key={item.id}
                size="small"
                label={eventsFromParent ? `${item.label} · מהאב` : item.label}
              />
            ),
          )}
          {domains
            .filter((domain) => domainIds.includes(domain.id))
            .map((domain) => (
              <Chip
                key={domain.id}
                size="small"
                variant="outlined"
                label={
                  parentDomainIds.includes(domain.id) &&
                  !(branch.activities ?? []).some((row) => row.domainId === domain.id)
                    ? `${domain.name} · מהאב`
                    : domain.name
                }
              />
            ))}
        </Stack>
        {branch.volunteerCapacity != null ? (
          <Typography sx={{ fontSize: 14 }}>
            קיבולת: {branch.volunteerCapacity} מתנדבים
          </Typography>
        ) : null}
        {assignments.length > 0 ? (
          <Stack gap={1}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "text.secondary" }}>
              שיבוצים
            </Typography>
            {assignments.map((row) => (
              <Typography key={row.id} sx={{ fontSize: 14 }}>
                {row.project?.name}: {row.volunteerCount} מתנדבים · {row.address}
              </Typography>
            ))}
          </Stack>
        ) : null}
        <Stack gap={1.25}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "text.secondary" }}>
            חיוג
          </Typography>
          {phones.length === 0 ? (
            <Typography color="warning.main" sx={{ fontSize: 13 }}>
              אין טלפון לשלוחה או לארגון האב.
            </Typography>
          ) : (
            phones.map((phone, index) => (
              <Stack key={`${phone.roleId}-${phone.phone}-${index}`} gap={0.5}>
                <Typography sx={{ fontSize: 13 }}>
                  {phone.label}
                  {phone.fromParent ? " · מהארגון האב" : ""} · {phone.name}
                </Typography>
                <PhoneNumberCell phoneNumber={phone.phone} />
                {phone.email ? (
                  <Typography sx={{ fontSize: 13 }}>{phone.email}</Typography>
                ) : null}
              </Stack>
            ))
          )}
        </Stack>
      </Stack>
      <Stack gap={1} sx={{ p: 2.5, borderTop: 1, borderColor: "divider" }}>
        <Button variant="contained" onClick={onLogCall} sx={primaryActionSx}>
          תעד שיחה
        </Button>
        <Stack direction="row" gap={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={isVolunteerOrgBranch(branch) ? onEditBranch : onEditParent}
            sx={quietButtonSx}
          >
            {isVolunteerOrgBranch(branch) ? "עריכת שלוחה" : "עריכת ארגון"}
          </Button>
          {branch.parent ? (
            <Button fullWidth variant="outlined" onClick={onEditParent} sx={quietButtonSx}>
              ארגון אב
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
}
